import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function QuestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef();
  const { data: q, isLoading, refetch } = useQuery({ queryKey: ["quest", id], queryFn: async () => (await api.get(`/quests/${id}`)).data });
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  if (isLoading || !q) return <Loader />;
  const sub = q.my_submission;
  const locked = q.my_status === "approved";
  const type = q.submission_type;

  const submit = async () => {
    setBusy(true);
    try {
      let file_id = null, file_url = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = up.data.id; file_url = up.data.url;
      }
      await api.post("/submissions", { quest_id: id, text, link, selected_option: selectedOption, file_id, file_url });
      toast.success("🎉 Quest Submitted! Your Story Coach will review it.");
      qc.invalidateQueries();
      refetch();
    } catch (e) { toast.error("Oops! Your story got stuck backstage. 🎭 " + apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="quest-detail">
      <button onClick={() => navigate("/app/quests")} className="flex items-center gap-1 text-sm font-bold text-csh-purple"><Icons.ChevronLeft size={16} /> All Quests</button>

      <div className="rounded-3xl gradient-purple p-6 text-white">
        <div className="text-sm font-bold uppercase text-white/70">Quest #{q.number} · Week {q.week}</div>
        <h1 className="font-head text-3xl">{q.name}</h1>
        <div className="mt-3 flex flex-wrap gap-2 text-sm font-bold">
          <span className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1"><Icons.Star size={14} /> +{q.points} XP</span>
          {q.badge && <span className="flex items-center gap-1 rounded-full bg-csh-orange px-3 py-1"><Icons.Medal size={14} /> {q.badge.name}</span>}
          <span className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 capitalize"><Icons.Gauge size={14} /> {q.difficulty}</span>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
        <h3 className="font-head text-xl text-csh-blue">What you need to do</h3>
        <p className="mt-2 whitespace-pre-line text-csh-grey">{q.instructions}</p>
        {q.tips && <div className="mt-4 rounded-2xl bg-csh-orange/10 p-4 text-sm"><b className="text-csh-orange">💡 Tip:</b> {q.tips}</div>}
        {q.deadline && <p className="mt-3 text-sm text-muted-foreground">📅 Deadline: {q.deadline}</p>}
      </div>

      {/* Current submission state */}
      {sub && (
        <div data-testid="submission-status" className={`rounded-3xl p-6 border-2 ${
          q.my_status === "approved" ? "bg-green-50 border-green-300" :
          q.my_status === "resubmit" ? "bg-blue-50 border-blue-300" : "bg-yellow-50 border-yellow-300"}`}>
          <div className="flex items-center gap-2 font-head text-xl text-csh-blue">
            {q.my_status === "approved" ? "✅ Approved!" : q.my_status === "resubmit" ? "🔄 Coach asked for a resubmission" : "🟡 Pending Review"}
          </div>
          {sub.feedback && <p className="mt-2 rounded-xl bg-white p-3 text-sm"><b>Coach feedback:</b> {sub.feedback}</p>}
          {sub.rating && <p className="mt-2 text-sm">⭐ Rating: {sub.rating}/5</p>}
        </div>
      )}

      {/* Submission form */}
      {!locked && (
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5 space-y-4" data-testid="submission-form">
          <h3 className="font-head text-xl text-csh-blue">{sub ? "Resubmit your Quest" : "Submit your Quest"}</h3>

          {(type === "video" || type === "audio" || type === "image") && (
            <div>
              <input ref={fileRef} type="file" data-testid="quest-file-input"
                accept={type === "video" ? "video/*" : type === "audio" ? "audio/*" : "image/*"}
                onChange={(e) => setFile(e.target.files[0])} className="hidden" />
              <button onClick={() => fileRef.current?.click()}
                className="flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-csh-purple/40 bg-csh-purple/5 py-8 hover:bg-csh-purple/10">
                <Icons.UploadCloud size={32} className="text-csh-purple" />
                <span className="font-bold text-csh-blue">{file ? file.name : `Upload ${type}`}</span>
                <span className="text-xs text-muted-foreground">Tap to choose a file from your device</span>
              </button>
            </div>
          )}

          {(type === "text" || type === "story") && (
            <Textarea data-testid="quest-text-input" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Write your story here..." rows={5} className="rounded-2xl border-2" />
          )}

          {type === "link" && (
            <Input data-testid="quest-link-input" value={link} onChange={(e) => setLink(e.target.value)}
              placeholder="Paste your video/link URL" className="rounded-xl border-2 py-6" />
          )}

          {(type === "mcq" || type === "quiz") && (
            <div className="space-y-2">
              {(q.options || []).map((opt, i) => (
                <button key={i} onClick={() => setSelectedOption(i)}
                  className={`flex w-full items-center gap-2 rounded-xl border-2 px-4 py-3 text-left font-semibold ${
                    selectedOption === i ? "border-csh-purple bg-csh-purple/10" : "border-black/10"}`}>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs">{String.fromCharCode(65 + i)}</span> {opt}
                </button>
              ))}
            </div>
          )}

          {(type === "completion" || type === "parent") && (
            <p className="rounded-2xl bg-csh-blue/5 p-4 text-sm">Tap submit once you've completed this activity{type === "parent" ? " with your parent" : ""}! 🎉</p>
          )}

          <Button data-testid="submit-quest-btn" onClick={submit} disabled={busy}
            className="w-full rounded-full gradient-orange py-6 font-head text-lg uppercase text-white hover:scale-[1.02] active:scale-95">
            {busy ? <Icons.Loader2 className="animate-spin" /> : "Submit Quest"}
          </Button>
        </div>
      )}
    </div>
  );
}
